import { useGetMasterListByUrlQuery } from 'app/services/ahuComponentFormsApi';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import { COMPONENT_FORM_CONFIGS } from '../componentFormConfigs';
import { useComponentForm } from '../useComponentForm';
import { FormSubtab, handleTabToNext, type OptionRow } from '../formKit';
import {
  cmsAirStreams,
  MBareInput,
  MInput,
  MSelect,
  RequiredFieldNote,
  SBareInput,
} from './formCommon';
import type { ComponentFormProps } from './ComponentFormHost';

/**
 * Phase 6e2a — UDC form (§50.1 row; legacy LEGACY Backbone view 287 + tmpl
 * 365 — NOT BaseComponentView; parity 328):
 *  - model = ahu/user_defined_components/<component.id>;
 *  - `.udc_field` writes (ported onto the shared engine — DEVIATION: legacy
 *    persists per-change via backbone.plus; here the §49.2 1 s debounce
 *    engine carries the PATCH, same changed-fields-only bodies);
 *  - supplied_by_id==1 swaps the UDC pricing collection;
 *  - tag-strip rename `User Defined : <name>` → `change:section:tag:canvas`;
 *  - collections from stateStorage equivalents: supplyed_by/udc_type/
 *    powered_by/voltages/udc.pricing.methods (§20 #98).
 */
const config = COMPONENT_FORM_CONFIGS.user_defined_component;

const UDC_PRICING_URL =
  '/api/v1.0/master/v1.0.0/ahu/user_defined_component_accessory_pricing_methods/';

const UdcForm = ({ row, section, section2, cms }: ComponentFormProps) => {
  const api = useComponentForm({
    config,
    row,
    section,
    section2,
    firstSubtab: 'properties',
  });
  const { model } = api;

  const { data: voltages } = useGetMasterListByUrlQuery('/api/v1.0/master/v1.0.0/ahu/voltages/');
  const { data: poweredBy } = useGetMasterListByUrlQuery('/api/v1.0/master/v1.0.0/ahu/powered_by/');
  const { data: udcTypes } = useGetMasterListByUrlQuery('/api/v1.0/master/v1.0.0/ahu/udc_types/');
  const { data: suppliedBy } = useGetMasterListByUrlQuery(
    '/api/v1.0/master/v1.0.0/ahu/supplyed_by/' // [sic] legacy route (verify 6e2a-6)
  );
  const { data: udcPricing } = useGetMasterListByUrlQuery(UDC_PRICING_URL);

  if (api.loading || !model) {
    return <div className="preloader dotted-preloader active" />;
  }
  const sub = (name: string) => !!api.subtabs[name];

  // supplied_by_id == 1 → UDC pricing collection; otherwise the methods list
  // is EMPTY and the pricing sublist renders non-clickable (legacy form.js
  // 214-224 + tmpl supplied_by gate).
  const suppliedByVendor = Number(model.get('supplied_by_id')) === 1;
  const pricing = (suppliedByVendor ? udcPricing : []) ?? [];

  const commitName = (raw: string) => {
    api.commitModelField('udc_name_id', raw);
    // tag-strip rename — `change:section:tag:canvas` after the name change.
    const list = (udcTypes ?? []) as OptionRow[];
    const picked = list.find((r) => String(r.id) === raw);
    ahuBoardBus.emit(
      'change:section:tag:canvas',
      section.id,
      `User Defined : ${String(picked?.name ?? '')}`
    );
  };

  return (
    <>
      <div className="component-form-heading clearfix">
        <h2 className="left hide">
          user defined
          {!!model.get('udc_name') && (
            <span className="uds-tag">: {String(model.get('udc_name'))}</span>
          )}
        </h2>
      </div>
      <form onKeyDown={handleTabToNext}>
        <ul className="inputs-group max-height-auto">
          <FormSubtab type="user_defined_component" cid={row.id} name="properties" label="Properties" open={sub('properties')} onToggle={api.toggleSubtab}>
            <li>
              <div className="single-line">
                <select
                  name="udc_name_id"
                  className="udc_field"
                  value={String(model.get('udc_name_id') ?? 'None')}
                  onChange={(e) => commitName(e.target.value)}
                >
                  <option value="None" className="hide">- -</option>
                  {((udcTypes ?? []) as OptionRow[]).map((r) => (
                    <option key={String(r.id)} value={String(r.id)}>
                      {String(r.name ?? '')}
                    </option>
                  ))}
                </select>
                <span className="label"><span>Component</span></span>
              </div>
            </li>
            <MInput api={api} name="qty" label="Qty" formatter="none" />
            <MInput api={api} name="description" label="Description" formatter="none" />
            <MInput api={api} name="udc_mfg" label="Manufacturer" formatter="none" />
            <MInput api={api} name="udc_model" label="Model" formatter="none" />
            <MInput api={api} name="udc_mpn" label="Mfg's Part Number" formatter="none" />
            <li>
              <div className="multiply-total multiply user-def">
                <span className="unit">in</span>
                <MBareInput api={api} name="width" formatter="none" extraClass="small-input" fieldClass="udc_field" />
                <span className="multiply-sign">X</span>
                <MBareInput api={api} name="height" formatter="none" extraClass="small-input" fieldClass="udc_field" />
                <span className="multiply-sign">X</span>
                <MBareInput api={api} name="length" formatter="none" extraClass="small-input" fieldClass="udc_field" />
                <span className="label">
                  <span>Width</span> x <span>Height</span> x <span>Length</span>
                </span>
              </div>
            </li>
            <MSelect api={api} name="supplied_by_id" label="Supplied by" options={(suppliedBy ?? []) as OptionRow[]} hideNone />
            <li>
              <div className="multiply-total multiply">
                <span className="unit">lb</span>
                <MBareInput api={api} name="shipping_weight_each" formatter="none" extraClass="small-input" fieldClass="udc_field" />
                <span className="multiply-sign">X</span>
                <MBareInput api={api} name="qty" formatter="none" readOnly extraClass="small-input" fieldClass="udc_field" />
                <MBareInput api={api} name="total_shipping_weight" formatter="none" readOnly extraClass="small-input" fieldClass="udc_field" />
                <span className="label">Shipping Weight</span>
              </div>
            </li>
            <li>
              <div className="multiply-total multiply">
                <span className="unit">lb</span>
                <MBareInput api={api} name="operating_weight_each" formatter="none" extraClass="small-input" fieldClass="udc_field" />
                <span className="multiply-sign">X</span>
                <MBareInput api={api} name="qty" formatter="none" readOnly extraClass="small-input" fieldClass="udc_field" />
                <MBareInput api={api} name="total_operating_weight" formatter="none" readOnly extraClass="small-input" fieldClass="udc_field" />
                <span className="label">Operating Weight</span>
              </div>
            </li>
          </FormSubtab>

          <FormSubtab type="user_defined_component" cid={row.id} name="electrical" label="Electrical" open={sub('electrical')} onToggle={api.toggleSubtab}>
            <MInput api={api} name="power_w" label="Power" unit="W" formatter="none" />
            <MSelect api={api} name="voltage_id" label="Voltage" options={(voltages ?? []) as OptionRow[]} />
            <MInput api={api} name="fla" label="Full Load Amp" unit="Amp" formatter="none" />
            <MSelect api={api} name="powered_by_id" label="Powered By" options={(poweredBy ?? []) as OptionRow[]} />
          </FormSubtab>

          <FormSubtab type="user_defined_component" cid={row.id} name="pricing" label="Pricing" open={sub('pricing')} onToggle={api.toggleSubtab} sublistClass={suppliedByVendor ? undefined : ' non-clickable '}>
            <MSelect api={api} name="pricing_method_id" label="Pricing Method" options={pricing as OptionRow[]} hideNone />
            {Number(model.get('pricing_method_id')) !== 1 && (
              <li>
                <div className="frequency">
                  <span className="unit">US$</span>
                  <MBareInput api={api} name={Number(model.get('pricing_method_id')) === 3 ? 'net_price_product_user' : 'cost_product_user'} extraClass="medium-input" fieldClass="udc_field" />
                  <MBareInput api={api} name={Number(model.get('pricing_method_id')) === 3 ? 'net_price_freight_user' : 'cost_freight_user'} extraClass="medium-input" fieldClass="udc_field" />
                  <span className="label">Mtl | Freight</span>
                </div>
              </li>
            )}
          </FormSubtab>

          <FormSubtab type="user_defined_component" cid={row.id} name="clearances" label="Clearances" open={sub('clearances')} onToggle={api.toggleSubtab}>
            <li>
              <div className="multiply-total">
                <span className="unit">in</span>
                <MBareInput api={api} name="available_clear_ups_in_section" formatter="none" extraClass="medium-input" fieldClass="udc_field" />
                <MBareInput api={api} name="available_clear_downs_in_section" formatter="none" extraClass="medium-input" fieldClass="udc_field" />
                <span className="label">
                  <span>In section upstream</span> | <span>downstream</span>
                </span>
              </div>
            </li>
            <li>
              <div className="multiply-total">
                <span className="unit">in</span>
                <MBareInput api={api} name="min_clear_ups" formatter="none" extraClass="medium-input" fieldClass="udc_field" />
                <MBareInput api={api} name="min_clear_downs" formatter="none" extraClass="medium-input" fieldClass="udc_field" />
                <span className="label">
                  <span>Minimum upstream</span> | <span>downstream</span>
                </span>
              </div>
            </li>
          </FormSubtab>

          <FormSubtab type="user_defined_component" cid={row.id} name="performance" label="Performance" open={sub('performance')} onToggle={api.toggleSubtab}>
            <li>
              <div className="single-line">
                <span className="selection medium-selection">
                  {(() => {
                    const hit = cmsAirStreams(cms).find(
                      // eslint-disable-next-line eqeqeq
                      (r) => r.id == model.get('air_stream_id')
                    );
                    return hit ? <span>{String(hit.name ?? '')}</span> : null;
                  })()}
                </span>
                <select
                  name="air_stream_id"
                  className="medium-input udc_field"
                  value={String(model.get('air_stream_id') ?? 'None')}
                  onChange={(e) => api.commitModelField('air_stream_id', e.target.value)}
                >
                  <option value="None">- -</option>
                  {cmsAirStreams(cms).map((r) => (
                    <option key={String(r.id)} value={String(r.id)}>
                      {String(r.name ?? '')}
                    </option>
                  ))}
                </select>
                <span className="unit">CFM</span>
                <MBareInput api={api} name="airflow_actual" formatter="integer" extraClass="medium-input" fieldClass="udc_field" />
                <span className="label ">Air Stream &amp; Flow</span>
              </div>
            </li>
            <li>
              <div className="multiply">
                <span className="unit">fpm</span>
                <MBareInput api={api} name="face_velocity_actual" formatter="none" extraClass="medium-input" fieldClass="udc_field" />
                <span className="unit">in WC</span>
                <MBareInput api={api} name="apd" formatter="none" extraClass="medium-input udc-medium" fieldClass="udc_field" />
                <span className="label">
                  <span>Face Velocity</span> | <span>APD</span>
                </span>
              </div>
            </li>
            <li>
              <div className="efficiency-mech">
                <span className="unit temp"> F </span>
                {/* Dev-sync J6 — pure rename (user_defined_component
                    form.tmpl +338..339; survey §A3a: the _summer aliases are
                    read/write @property views of the same legacy columns).
                    Display falls back to the bare column for older cache
                    rows, matching EvapPackForm's J4 rename. */}
                <SBareInput
                  api={api}
                  name="eadb_temp_summer"
                  value={(section as Record<string, unknown>).eadb_temp_summer ?? section.eadb_temp}
                />
                <SBareInput
                  api={api}
                  name="eawb_temp_summer"
                  value={(section as Record<string, unknown>).eawb_temp_summer ?? section.eawb_temp}
                />
                <span className="label">
                  <span>EADB</span> | <span>EAWB</span>
                </span>
              </div>
            </li>
            <li>
              <div className="efficiency-mech">
                <span className="unit temp"> F </span>
                <MBareInput api={api} name="ladb_temp" formatter="none" extraClass="medium-input" fieldClass="udc_field" />
                <MBareInput api={api} name="lawb_temp" formatter="none" extraClass="medium-input" fieldClass="udc_field" />
                <span className="label">
                  <span>LADB</span> | <span>LAWB</span>
                </span>
              </div>
            </li>
          </FormSubtab>

        </ul>
      </form>
      {/* components/user_defined_component/tmpl/form.tmpl — the footer note closes every legacy
          component form; this port dropped it (same omission as the coil
          panel this branch fixes). */}
      <RequiredFieldNote />
    </>
  );
};

export default UdcForm;
